/**
 * Logs the `CPT_NEW_DESCRIPTION_SAVED` BigQuery event for the CPT codes
 * screen. Two call sites converge here:
 *
 * - `entry_flag: "existing"` — user picked an option from the searchable
 *   card dropdown (fired from `cptCodes.tsx#onOptionClick`).
 * - `entry_flag: "new"` — user typed a free-text description and confirmed
 *   it in {@link CptNewDescriptionModal} (fired from `CptCodesModals.tsx#onSave`).
 *
 * Centralizing the event keeps the BigQuery payload schema consistent across
 * both flows and removes duplicated `apiWorkerManager.enqueueAndProcess`
 * boilerplate from the page/modal layers.
 *
 * @module logCptNewDescriptionSavedEvent
 */
import {
  getCurrentUserEmail,
  getCurrentUserId,
} from "../../../../../api/services/memberManagementService";
import { GeneralOrderDetails } from "../../../../../data-model/medicalPaOrdersModel";
import { ScreenNamesForBigQuery } from "../../../../../enums/eventsData";
import { EventsName } from "../../../../../enums/eventsName";
import { getOrgIdForFetchExternalWorklist } from "../../../../../utils/organizationHelper";
import { apiWorkerManager } from "../../../../../workerManagers/workerManager";

/** Distinguishes between selecting an existing option and creating a new one. */
export type CptNewDescriptionEntryFlag = "new" | "existing";

/** Arguments accepted by {@link logCptNewDescriptionSavedEvent}. */
interface LogCptNewDescriptionSavedEventParams {
  /** Human-readable CPT description being logged. */
  cptDescription: string;
  /** J-code associated with the row (empty string if unknown). */
  cptCode: string;
  /** Source of the description: `"existing"` for a dropdown pick, `"new"` for a typed entry. */
  entryFlag: CptNewDescriptionEntryFlag;
  /** General order details (unwrapped — Postgres `.order` payload). */
  generalOrderDetails: GeneralOrderDetails | null | undefined;
}

/**
 * Enqueues a `CPT_NEW_DESCRIPTION_SAVED` BigQuery event with the standard
 * CPT screen payload. Caller is responsible for resolving `cptDescription`
 * and `cptCode` before invoking this helper.
 *
 * @param params - Description, J-code, entry flag, and order metadata.
 */
export const logCptNewDescriptionSavedEvent = ({
  cptDescription,
  cptCode,
  entryFlag,
  generalOrderDetails,
}: LogCptNewDescriptionSavedEventParams): void => {
  apiWorkerManager.enqueueAndProcess({
    patient_id: generalOrderDetails?.demographics?.patient_id ?? "",
    order_id: generalOrderDetails?.order_id ?? "",
    org_id: getOrgIdForFetchExternalWorklist() || "",
    user_id: getCurrentUserId() ?? "",
    user_email: getCurrentUserEmail() ?? "",
    event_name: EventsName.CPT_DESCRIPTION_CHANGED,
    additional_data: {
      screen_name: ScreenNamesForBigQuery.CPT_CODE_SCREEN,
      cpt_description: cptDescription,
      cpt_code: cptCode,
      change_type: "user",
      entry_flag: entryFlag,
    },
  });
};
